import {
    Checkbox,
    createStyles,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    StyledComponentProps,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import * as React from "react";

import { Chip, IChip, RelodeTheme } from "../";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                backgroundColor: "transparent",
                marginBottom: 16,
                position: "relative",
                display: "flex",
                flexDirection: "column",
            },
            description: {
                top: 0,
                left: 0,
                position: "absolute",
            },
            tagList: {
                marginTop: 24,
                "& div:last-child": {
                    marginRight: 0,
                },
                "& div": {
                    marginRight: 8,
                    marginBottom: 8,
                    [theme.breakpoints.down("md")]: {
                        marginRight: 0,
                    },
                },
            },
            error: {
                color: theme.palette.error.main,
            },
            helperText: {
                marginBottom: theme.spacing(3),
            },
            labels: {
                display: "inline-block",
                width: 80,
                fontSize: 16,
                [theme.breakpoints.down("md")]: {
                    flexBasis: "25%",
                },
            },
            daysAndTimesFlex: {
                [theme.breakpoints.down("md")]: {
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                },
            },
            daysAndTimesReviewWizard: {
                [theme.breakpoints.down("md")]: {
                    width: "90",
                    marginLeft: theme.spacing(1),
                },
            },
            chip: {
                [theme.breakpoints.down("md")]: {
                    paddingLeft: theme.spacing(1),
                },
            },
        }),
    { name: "ChipCollectionBestTimes" }
);

interface OwnProps {
    baseId: string;
    style?: React.CSSProperties;
    description?: string;
    chips: IChip[] | IChip;
    isExclusive?: boolean; // there can be only one selected
    hideSelectAll?: boolean;
    isReviewWizard?: boolean;
    dataCy?: string;
    onChipClick?: (id: number, value?: any) => void;
    onSelectAll?: (checked: boolean) => void;
    daysAndTimes: [
        {
            day: string;
            times: Array<{
                canEdit?: boolean;
                canMatch?: boolean;
                createUserId?: number;
                isMatched?: boolean;
                tagId: number;
                tagName: string;
                tagTypeId: number;
                tagTypeName: string;
            }>;
        }
    ];
    error?: boolean;
    helperText?: string;
}

export interface ChipCollectionBestTimesProps
    extends OwnProps,
        StyledComponentProps<any> {}

export const ChipCollectionBestTimes = (props: OwnProps) => {
    const {
        chips,
        isExclusive,
        onChipClick,
        onSelectAll,
        style,
        baseId,
        description,
        daysAndTimes,
        error,
        helperText,
        hideSelectAll,
        isReviewWizard,
        dataCy,
    } = props;
    const classes = useStyles({});
    const [allSelected, setAllSelected] = React.useState(false);

    const handleChipClick = (id: number, value?: IChip) => {
        if (chips instanceof Array && isExclusive === true) {
            chips.forEach((chip, i) => {
                if (chip.id !== id) {
                    chip.isMatched = false;
                    chip.canEdit = true;
                }
            });
        }

        setAllSelected(false);
        if (onChipClick) onChipClick(id, value);
    };

    const handleSelectAll = (checked: boolean) => {
        setAllSelected(checked);
        onSelectAll(checked);
    };

    const renderTags = () => (
        <>
            <div>
                {!hideSelectAll ? (
                    <FormControlLabel
                        id={`${baseId}SelectAllCheckbox`}
                        control={
                            <Checkbox
                                checked={allSelected}
                                onChange={(e, c) => handleSelectAll(c)}
                                color="primary"
                            />
                        }
                        label="select all below"
                    />
                ) : null}
            </div>
            {daysAndTimes.map(day => (
                <div
                    key={`day-collection-${day.day}`}
                    className={
                        isReviewWizard
                            ? classes.daysAndTimesReviewWizard
                            : classes.daysAndTimesFlex
                    }
                >
                    <div className={classes.labels}>{day.day}</div>
                    {isReviewWizard ? <br /> : null}
                    {day.times.map(chip => (
                        <Chip
                            key={`${chip.tagId}-${chip.tagName}`}
                            style={style}
                            value={chip}
                            label={chip.tagName}
                            id={chip.tagId}
                            selected={chip.isMatched}
                            enabled={chip.canEdit}
                            onChipClick={
                                onChipClick && chip.canEdit
                                    ? handleChipClick
                                    : undefined
                            }
                            dataCy={dataCy + `${chip.tagId}-chip`}
                        />
                    ))}
                </div>
            ))}
        </>
    );

    const labelClasses = classnames(classes.description, {
        [classes.error]: error,
    });
    const helperTextClasses = classnames(classes.helperText, {
        [classes.error]: error,
    });
    const extendedId = `${baseId}ChipCollectionBestTimes`;

    return (
        <div className={classes.root} id={`${extendedId}Wrapper`}>
            {description && (
                <InputLabel className={labelClasses} shrink>
                    {description}
                </InputLabel>
            )}
            <div className={classes.tagList} id={`${extendedId}TagList`}>
                {renderTags()}
            </div>
            {error && (
                <FormHelperText className={helperTextClasses}>
                    {helperText}
                </FormHelperText>
            )}
        </div>
    );
};
